<template>
  <section
    id="product-details"
    :class="{
      'details-optimization': shoppableRedesignOptimization,
      'uppercase-titles': !isMcom && productDetailsDrawer,
    }"
  >
    <header class="header">
      <h3 class="title">
        {{ productDetailsText }}
      </h3>
      <span class="product-id">
        Web ID: {{ productId }}
      </span>
    </header>

    <div
      class="expandable-wrapper"
      :class="[{ 'open' : isOpen }, spacingClass]"
      :style="isSwitchButtonAvailable ? {'--blur-height': CLOSE_WRAPPER_HEIGHT + 'px'} : {}"
    >
      <div
        ref="wrapper"
        class="wrapper"
        :class="additionalClasses"
      >
        <div
          v-if="isShownDefaultProductDescription || longDescriptionFromColumns?.isAvailable"
          data-auto="product-long-description-section"
          class="long-description medium"
        >
          <p
            v-if="isShownDefaultProductDescription"
            v-html="productDescription"
          />
          <div v-else-if="longDescriptionFromColumns?.isAvailable">
            <h4
              v-if="longDescriptionFromColumns.title"
              class="medium margin-bottom-xxs"
              v-html="longDescriptionFromColumns.title"
            />
            <span v-html="longDescriptionFromColumns.paragraph" />
          </div>
        </div>

        <ul class="details">
          <template v-for="item in additionalColumns">
            <li
              v-if="item.isAvailable"
              :key="item.title"
            >
              <h4
                class="medium margin-bottom-xxs"
                v-html="item.title"
              />
              <span v-html="item.paragraph" />
            </li>
          </template>

          <li
            v-if="isSummaryAvailable"
            ref="availableColumnsArr"
            class="column"
            data-auto="product-summary-section"
          >
            <h4 class="medium margin-bottom-xxs">
              {{ featuresText }}
            </h4>
            <Summary :open-as-initial-slideout="true" />
          </li>

          <li
            v-if="isSizeAndFitAvailable"
            ref="availableColumnsArr"
            class="column"
            data-auto="size-fit-section"
          >
            <h4 class="medium margin-bottom-xxs">
              Size & Fit
            </h4>
            <SizeAndFit />
          </li>

          <li
            v-if="isIngredientsAvailable"
            ref="availableColumnsArr"
            class="column"
            data-auto="product-dimensions-section"
            data-testid="product-ingredients-section"
          >
            <h4 class="medium margin-bottom-xxs">
              {{ dimensionsTitle }}
            </h4>
            <Ingredients />
          </li>

          <li
            v-if="isDimensionsAvailable"
            ref="availableColumnsArr"
            class="column"
            data-auto="product-dimensions-section"
            data-testid="product-dimensions-section"
          >
            <h4 class="medium margin-bottom-xxs">
              Product Dimensions
            </h4>
            <Dimensions />
          </li>

          <li
            v-if="isMaterialAndCareAvailable"
            ref="availableColumnsArr"
            class="column"
            data-auto="materials-care-section"
          >
            <h4 class="medium margin-bottom-xxs">
              Materials & Care
            </h4>
            <MaterialsAndCare :open-as-initial-slideout="true" />
          </li>

          <li
            v-if="isInstructionsAvailable"
            ref="availableColumnsArr"
            class="column"
            data-auto="instructions-section"
          >
            <h4 class="medium margin-bottom-xxs">
              Instructions
            </h4>
            <Instructions />
          </li>

          <li
            v-if="isTermsAndConditionsAvailable"
            ref="availableColumnsArr"
            class="column"
            data-auto="terms-conditions-section"
          >
            <h4 class="medium margin-bottom-xxs">
              Terms & Conditions
            </h4>
            <TermsAndConditions />
          </li>

          <li
            v-if="isShippingAndReturnsAvailable"
            ref="availableColumnsArr"
            class="column"
            data-auto="shipping-returns-section"
          >
            <h4 class="medium margin-bottom-xxs">
              Shipping & Returns
            </h4>
            <ShippingAndReturns />
          </li>

          <li
            v-if="isWhiteGloveDeliveryAvailable"
            ref="availableColumnsArr"
            class="column"
            data-auto="white-glove-delivery-section"
          >
            <h4 class="medium margin-bottom-xxs">
              {{ bigTicketDeliveryMethods?.length ? 'Delivery Methods' : 'White Glove Delivery' }}
            </h4>
            <WhiteGloveDelivery />
          </li>

          <li
            v-if="isSalesFloorWidgetAvailable"
            ref="availableColumnsArr"
            class="column"
            data-auto="sales-floor-widget-section"
          >
            <SalesFloorWidget />
          </li>
        </ul>
      </div>

      <div
        v-if="isSwitchButtonAvailable"
        class="switch-wrapper"
        @click="handleButtonClick"
      >
        <button
          class="switch link-med"
        >
          {{ buttonText }}
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
/* istanbul ignore file */
import {
  computed, ref, onMounted, watch, defineAsyncComponent,
} from 'vue';
import { useToggle } from '@vueuse/core';
import { useProduct } from '@modules/product';
import { useFeatureEligibility, useKillswitches } from '@modules/featureControl';
import { useCommonProductGetters } from '@composables/useCommonProductGetters';
import { useDeviceType } from '@composables/utils/useDeviceType';
import { useBrand } from '@composables/useBrand';
import { useSlideout } from '@composables/utils/useSlideout';
import { useExperiments } from '@composables/useExperiments';
import SalesFloorWidget from '@components/SalesFloorWidget';
import {
  useProductDetailsProvide,
  useProductDetails,
  useProductDetailsAvailabilityProvide,
  useProductDetailsAvailability,
} from '../composables';
import tagViewMoreViewLessToggle from '../../../features/analytics/productDetails';

import Summary from './Summary';
import Dimensions from './Dimensions';
import MaterialsAndCare from './MaterialsAndCare';
import Instructions from './Instructions';
import TermsAndConditions from './TermsAndConditions';
import SizeAndFit from './SizeAndFit';
import WhiteGloveDelivery from './WhiteGloveDelivery';
import ShippingAndReturns from './ShippingAndReturns';
import Ingredients from './Ingredients';
import { splitTextToColumns } from '../utils';

useProductDetailsProvide();
useProductDetailsAvailabilityProvide();

const { productId, productData: product } = useProduct();
const { isMcom } = useBrand();
const { isMobile } = useDeviceType();
const { bigTicketDeliveryMethods } = useCommonProductGetters();
const { productDescription, dimensionsTitle } = useProductDetails();
const { isShoppable, shoppableRedesignOptimization } = useFeatureEligibility();
const {
  isSummaryAvailable,
  isSizeAndFitAvailable,
  isIngredientsAvailable,
  isDimensionsAvailable,
  isMaterialAndCareAvailable,
  isInstructionsAvailable,
  isTermsAndConditionsAvailable,
  isShippingAndReturnsAvailable,
  isWhiteGloveDeliveryAvailable,
  isSalesFloorWidgetAvailable,
} = useProductDetailsAvailability();
const { killswitches } = useKillswitches();

const { showSlideout } = useSlideout();
const { experimentFlags: { productDetailsDrawer } } = useExperiments();

const CLOSE_WRAPPER_HEIGHT = isShoppable ? 120 : 240;
const spacingClass = shoppableRedesignOptimization ? 'margin-top-s' : 'medium-margin-top-s small-margin-top-xs';
const wrapper = ref(null);
const isSwitchButtonAvailable = ref(false);

// For more information regarding this regExp, please refer to my comment there https://macystech.atlassian.net/browse/UMP-1742?focusedCommentId=3451197
const TITLE_REGEXP = /(?:<b>)?([A-Z0-9\s&;'’]+):?(?:<\/b>)?:?(?=\s*<br>|$)/g;
const TITLE_REGEXP_OPTIMIZED = /(?:<b>)?([A-Z0-9][A-Z0-9&;'’\s*-]{2,}):?(?:<\/b>)?:?(?=\s*<br>|$)/g;
const regExp = (shoppableRedesignOptimization || killswitches.isPDPEnhancementNEnabled) ? TITLE_REGEXP_OPTIMIZED : TITLE_REGEXP;

const descriptionColumns = computed(() => (isMcom
  ? [] : splitTextToColumns(productDescription.value, regExp, killswitches.isPDPEnhancementNEnabled)));
const longDescriptionFromColumns = computed(() => descriptionColumns.value[0]);
const isShownDefaultProductDescription = computed(() => (descriptionColumns.value.length === 0 || !longDescriptionFromColumns.value?.isAvailable)
  && !!productDescription.value);
const additionalColumns = computed(() => (productDescription.value.length ? descriptionColumns.value.slice(1) : []));

const columnsArr = ref([
  ...additionalColumns.value,
  isSummaryAvailable.value,
  isSizeAndFitAvailable,
  isIngredientsAvailable,
  isDimensionsAvailable,
  isMaterialAndCareAvailable,
  isInstructionsAvailable,
  isTermsAndConditionsAvailable,
  isShippingAndReturnsAvailable,
  isWhiteGloveDeliveryAvailable,
  isSalesFloorWidgetAvailable,
]);
const availableColumnsCount = computed(() => columnsArr.value.filter((c) => c).length);

const [isOpen, toggle] = useToggle(false);

const viewMoreText = productDetailsDrawer ? 'View more' : 'View More';
const viewLessText = productDetailsDrawer ? 'View less' : 'View Less';
const featuresText = !isMcom && productDetailsDrawer ? 'Features' : 'Product Features';
const productDetailsText = isMcom && productDetailsDrawer ? 'Product details' : 'Product Details';
const buttonText = computed(() => (isOpen.value ? viewLessText : viewMoreText));

const additionalClasses = computed(() => ({
  'long-description-available': !!productDescription.value || !!longDescriptionFromColumns.value,
  'three-columns': availableColumnsCount.value === 3,
  'more-than-three': availableColumnsCount.value > 3,
  'less-than-three': availableColumnsCount.value < 3,
  'less-than-two': availableColumnsCount.value < 2,
}));

function getSwitchButtonAvailability() {
  if (isMobile.value) return true;

  const wrapperClientHeight = wrapper.value.clientHeight || 0;
  return wrapperClientHeight > CLOSE_WRAPPER_HEIGHT;
}

const showProductDetailsDrawer = () => {
  showSlideout({
    id: 'product-details-drawer',
    title: productDetailsText,
    component: defineAsyncComponent(/* istanbul ignore next */ () => import(/* webpackChunkName: "ProductDetailsDrawer" */ './slideouts/ProductDetailsDrawer')),
    backable: false,
    props: {
      productId,
      product,
    },
  });
};

const handleButtonClick = () => {
  if (productDetailsDrawer) {
    showProductDetailsDrawer();
    tagViewMoreViewLessToggle(true);
  } else {
    toggle(!isOpen.value);
  }
};

watch(isOpen, () => {
  tagViewMoreViewLessToggle(isOpen.value);
});

onMounted(() => {
  isSwitchButtonAvailable.value = getSwitchButtonAvailability();
});
</script>

<style lang="scss">
#product-details {
  ul.details {
    list-style: disc;

    ul {
      list-style: disc;
    }
  }
}
</style>

<style lang="scss" scoped>
@import "~settingSCSS";

#product-details {
  :deep(.salesfloor) {
    // Rewrite salesfloor styles
    margin-top: 0px!important;

    .sf-virtual-advisor--expanded {
      margin-top: 0px!important;
    }

    .sf-virtual-advisor--stylist-title {
      @include body(medium);
    }
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }

  .product-id {
    color: $gray-1-color;
  }

  .wrapper {
    display: grid;
    grid-template-areas:
      'longDescription'
      'details';
    grid-template-columns: 1fr;
    background-color: $background-color;
    @include padding-left-xxs;
    @include padding-right-xxs;
    @include padding-top-xs;
    @include padding-bottom-xs;
  }

  .details {
    list-style: none;
    margin: 0;
    padding: 0;
    grid-area: details;
    display: grid;
    grid-template-columns: 1fr;
    gap: rem-calc(16px);
  }

  .long-description {
    grid-area: longDescription;
  }

  .switch-wrapper {
    cursor: pointer;
  }
}

@include breakpoint(small up) {
  #product-details {
    .expandable-wrapper {
      height: var(--blur-height);
      overflow: hidden;
      position: relative;

      &.open {
        height: auto;

        .switch-wrapper {
          @include padding-bottom-xs;
          position: relative;
          height: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          background: $background-color;
          cursor: default;
        }
      }

      &:not(.open) {
        .switch-wrapper {
          position: absolute;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 96px;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.50) 28.13%, #FFF 72.4%);
        }

        .switch {
          position: absolute;
          bottom: 0;
        }
      }
    }
  }
}

@include breakpoint(small only) {
  #product-details {
    .product-id {
      @include body(small);
      color: $gray-1-color;
    }

    .long-description {
      @include margin-bottom-xs;
    }

    .expandable-wrapper {
      &.open {
        .switch-wrapper {
          @include padding-left-xxs;
          justify-content: flex-start;
        }
      }

      &:not(.open) {
        .switch {
          position: absolute;
          bottom: 0;
          left: 8px;
        }
      }
    }
  }
}

@include breakpoint(medium up) {
  #product-details {
    .switch {
      cursor: pointer;
    }

    .wrapper {
      grid-template-areas:
        'longDescription longDescription'
        'details details';
      column-gap: 0;
      @include padding-left-xs;
      @include padding-right-xs;
      @include padding-top-s;
      @include padding-bottom-s;

      &.long-description-available {
        row-gap: rem-calc(24px);
        column-gap: rem-calc(24px);
        grid-template-columns: 1fr 2fr;
        &.more-than-three,
        &.three-columns {
          grid-template-columns: 1fr 3fr;
        }
        &.less-than-two {
          grid-template-columns: 1fr 1fr;

          .details {
            grid-template-columns: 1fr;
          }
        }
      }
    }

    .details {
      gap: rem-calc(24px);
      grid-template-columns: repeat(2, 1fr);
    }

  }
}

@include breakpoint(large up) {
  #product-details {
    .wrapper {
      grid-template-areas:
        'longDescription details';
      column-gap: 0px;

      .details {
        grid-template-columns: repeat(3, 1fr);
      }

      &:not(.long-description-available) {
        grid-template-areas:
          'details details';

        &.less-than-two {
          row-gap: 0;

          .details {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        &.less-than-three {
          .details {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        &.more-than-three {
          .details {
            grid-template-columns: repeat(4, 1fr);
          }
        }
      }

      &.long-description-available {
        row-gap: 0;
        &.less-than-two,
        &.less-than-three {
          grid-template-columns: 1fr 2fr;

          .details {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped brand="mcom">
@import "~settingSCSS";

@include breakpoint(small only) {
  #product-details {
    .title {
      @include header(h5);
    }
  }
}
</style>

<style lang="scss" scoped brand="bcom">
@import "~settingSCSS";

#product-details {
  .column h4 {
    @include body(medium);
    text-transform: capitalize;
  }

  &.uppercase-titles {
    h4 {
      @include body(small);
      text-transform: uppercase;
      font-weight: bold;
    }
  }
}

@include breakpoint(small only) {
  #product-details {
    .title {
      @include header(h5);
    }
  }
  #product-details.details-optimization {
    .title {
      @include header(h3);
    }
  }
}
</style>

<style lang="scss" brand="bcom">
@import "~settingSCSS";

#product-details.uppercase-titles {
  b {
    @include body(small);
    text-transform: uppercase;
    font-weight: bold;
  }
}
</style>
